import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

interface CapacityPlanningData {
  periodo: {
    anno: number;
    mese_corrente: number;
    mesi_rimanenti: number;
  };
  summary: {
    fte_richiesti: number;
    fte_disponibili: number;
    gap_fte: number;
    budget_residuo_totale_gg: number;
    numero_risorse_attive: number;
  };
  progetti: {
    id: number;
    titolo: string;
    budget_residuo_gg: number;
    durata_mesi: number;
    fte_richiesti: number;
    data_inizio: string | null;
    data_fine: string | null;
  }[];
  fte_mensile: {
    anno: number;
    mese: number;
    fte_disponibili: number;
  }[];
}

const MESI_LABELS: { [key: number]: string } = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC'
};

const CapacityPlanningPage: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<CapacityPlanningData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/capacity-planning`, { headers });

      if (!response.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }

      const result = await response.json();
      setData(result);
    } catch (err: any) {
      console.error('Error loading capacity planning data:', err);
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Nessun dato disponibile'}</Alert>
      </Box>
    );
  }

  const { summary, periodo, progetti, fte_mensile } = data;
  const isGapPositive = summary.gap_fte > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Capacity Planning
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analisi FTE richiesti vs disponibili - Anno {periodo.anno} ({periodo.mesi_rimanenti} mesi rimanenti)
        </Typography>
      </Box>

      {/* Card Riepilogo FTE */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* FTE Richiesti */}
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AssessmentIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                FTE Richiesti
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              {summary.fte_richiesti.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
              Budget residuo: {summary.budget_residuo_totale_gg.toFixed(0)} gg
            </Typography>
          </CardContent>
        </Card>

        {/* FTE Disponibili */}
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <GroupIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                FTE Disponibili (Media)
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              {summary.fte_disponibili.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
              Risorse attive: {summary.numero_risorse_attive}
            </Typography>
          </CardContent>
        </Card>

        {/* Gap FTE */}
        <Card sx={{
          flex: '1 1 calc(25% - 16px)',
          minWidth: 250,
          background: isGapPositive
            ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {isGapPositive ? (
                <TrendingUpIcon sx={{ color: 'white', mr: 1 }} />
              ) : (
                <TrendingDownIcon sx={{ color: 'white', mr: 1 }} />
              )}
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Gap FTE
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              {summary.gap_fte > 0 ? '+' : ''}{summary.gap_fte.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
              {isGapPositive ? 'Servono più risorse' : 'Capacità in eccesso'}
            </Typography>
          </CardContent>
        </Card>

        {/* Periodo */}
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Periodo Analisi
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              {periodo.mesi_rimanenti}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
              Mesi rimanenti {periodo.anno}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabella FTE Mensile */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          FTE Disponibili per Mese
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Distribuzione FTE basata sulle allocazioni pianificate
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mese</TableCell>
                <TableCell align="right">FTE Disponibili</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fte_mensile.map((row) => (
                <TableRow key={`${row.anno}-${row.mese}`}>
                  <TableCell>
                    {MESI_LABELS[row.mese]} {row.anno}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.fte_disponibili.toFixed(2)}
                      color={row.fte_disponibili >= summary.fte_richiesti ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {fte_mensile.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nessuna allocazione pianificata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Tabella Progetti */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Dettaglio FTE Richiesti per Progetto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fabbisogno FTE calcolato su budget residuo e durata progetto
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Progetto</TableCell>
                <TableCell align="right">Budget Residuo (gg)</TableCell>
                <TableCell align="right">Durata (mesi)</TableCell>
                <TableCell align="center">Data Inizio</TableCell>
                <TableCell align="center">Data Fine</TableCell>
                <TableCell align="right">FTE Richiesti</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {progetti.map((progetto) => (
                <TableRow key={progetto.id} hover>
                  <TableCell>{progetto.titolo}</TableCell>
                  <TableCell align="right">{progetto.budget_residuo_gg.toFixed(2)}</TableCell>
                  <TableCell align="right">{progetto.durata_mesi}</TableCell>
                  <TableCell align="center">
                    {progetto.data_inizio
                      ? new Date(progetto.data_inizio).toLocaleDateString('it-IT')
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {progetto.data_fine
                      ? new Date(progetto.data_fine).toLocaleDateString('it-IT')
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={progetto.fte_richiesti.toFixed(2)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {progetti.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nessun progetto attivo
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CapacityPlanningPage;
